/**
 * SignalRConfiguration
 *
 * Centralized configuration management for SignalR connections
 * Single source of truth for all settings
 */

import type { SignalRConnectionConfig, BackoffConfig } from '../types';

export interface SignalRSettings {
  connection: SignalRConnectionConfig;
  reconnection: BackoffConfig & { strategy: 'exponential' | 'linear' | 'fixed' };
  logging: {
    enabled: boolean;
    level: 'debug' | 'info' | 'warn' | 'error';
    includeStackTraces: boolean;
    maxLogSize: number; // Max logs to keep in memory
  };
  timeouts: {
    connection: number; // ms
    invoke: number; // ms
    healthCheck: number; // ms
  };
  pipeline: {
    incomingPipelineId: string;
    outgoingPipelineId: string;
    timeout: number; // ms
  };
}

const DEFAULT_SETTINGS: SignalRSettings = {
  connection: {
    url: '',
    timeout: 30000,
    withCredentials: false,
    skipNegotiation: false,
    transports: ['WebSockets', 'LongPolling'],
  },
  reconnection: {
    strategy: 'exponential',
    initialDelayMs: 500,
    maxDelayMs: 60000,
    multiplier: 2,
    maxAttempts: 10,
  },
  logging: {
    enabled: true,
    level: 'info',
    includeStackTraces: true,
    maxLogSize: 1000,
  },
  timeouts: {
    connection: 30000,
    invoke: 10000,
    healthCheck: 5000,
  },
  pipeline: {
    incomingPipelineId: 'edgestream.default-incoming-v1',
    outgoingPipelineId: 'edgestream.default-outgoing-v1',
    timeout: 5000,
  },
};

/**
 * Configuration manager for SignalR
 * Provides typed access to all settings with validation
 */
export class SignalRConfiguration {
  private settings: SignalRSettings;
  private watchers: Map<string, Set<(newValue: unknown, oldValue: unknown) => void>> = new Map();

  constructor(overrides?: Partial<SignalRSettings>) {
    this.settings = this.mergeSettings(DEFAULT_SETTINGS, overrides);
  }

  /**
   * Get current settings (immutable)
   */
  getSettings(): Readonly<SignalRSettings> {
    return Object.freeze({ ...this.settings });
  }

  /**
   * Get specific setting by path
   * @param path - Dot-separated path (e.g., 'connection.timeout')
   */
  get<T = unknown>(path: string): T {
    const keys = path.split('.');
    let current: any = this.settings;

    for (const key of keys) {
      if (current?.[key] === undefined) {
        throw new Error(`Setting not found: ${path}`);
      }
      current = current[key];
    }

    return current as T;
  }

  /**
   * Update setting by path
   * @param path - Dot-separated path
   * @param value - New value
   */
  set(path: string, value: unknown): void {
    const keys = path.split('.');
    const lastKey = keys.pop();
    if (!lastKey) throw new Error('Invalid path');

    let current: any = this.settings;
    for (const key of keys) {
      if (!(key in current)) current[key] = {};
      current = current[key];
    }

    const oldValue = current[lastKey];
    current[lastKey] = value;

    // Notify watchers
    this.notifyWatchers(path, value, oldValue);
  }

  /**
   * Update multiple settings
   */
  update(overrides: Partial<SignalRSettings>): void {
    this.settings = this.mergeSettings(this.settings, overrides);
  }

  /**
   * Watch for changes to a setting
   * @param path - Setting path to watch
   * @param callback - Called when setting changes
   * @returns Function to unwatch
   */
  watch(path: string, callback: (newValue: unknown, oldValue: unknown) => void): () => void {
    if (!this.watchers.has(path)) {
      this.watchers.set(path, new Set());
    }
    this.watchers.get(path)!.add(callback);

    // Return unwatch function
    return () => {
      this.watchers.get(path)?.delete(callback);
    };
  }

  /**
   * Validate current configuration
   * @returns Array of validation errors (empty if valid)
   */
  validate(): string[] {
    const errors: string[] = [];

    // Connection validation
    if (!this.settings.connection.url) {
      errors.push('connection.url is required');
    }
    if (this.settings.connection.timeout < 1000) {
      errors.push('connection.timeout must be >= 1000ms');
    }

    // Reconnection validation
    if (this.settings.reconnection.maxAttempts < 1) {
      errors.push('reconnection.maxAttempts must be >= 1');
    }
    if (this.settings.reconnection.initialDelayMs < 100) {
      errors.push('reconnection.initialDelayMs must be >= 100ms');
    }
    if (this.settings.reconnection.maxDelayMs < this.settings.reconnection.initialDelayMs) {
      errors.push('reconnection.maxDelayMs must be >= initialDelayMs');
    }

    // Pipeline validation
    if (this.settings.pipeline.timeout < 1000) {
      errors.push('pipeline.timeout must be >= 1000ms');
    }

    return errors;
  }

  /**
   * Get string representation for debugging
   */
  toString(): string {
    return JSON.stringify(this.settings, null, 2);
  }

  /**
   * Reset to defaults
   */
  reset(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.notifyAllWatchers();
  }

  /**
   * Create a clone for testing/experimentation
   */
  clone(): SignalRConfiguration {
    return new SignalRConfiguration(JSON.parse(JSON.stringify(this.settings)));
  }

  // Private methods

  private mergeSettings(base: SignalRSettings, overrides?: Partial<SignalRSettings>): SignalRSettings {
    if (!overrides) return base;

    return {
      connection: { ...base.connection, ...overrides.connection },
      reconnection: { ...base.reconnection, ...overrides.reconnection },
      logging: { ...base.logging, ...overrides.logging },
      timeouts: { ...base.timeouts, ...overrides.timeouts },
      pipeline: { ...base.pipeline, ...overrides.pipeline },
    };
  }

  private notifyWatchers(path: string, newValue: unknown, oldValue: unknown): void {
    const callbacks = this.watchers.get(path);
    if (callbacks) {
      callbacks.forEach((cb) => {
        try {
          cb(newValue, oldValue);
        } catch (error) {
          console.error(`Watcher error for ${path}:`, error);
        }
      });
    }
  }

  private notifyAllWatchers(): void {
    this.watchers.forEach((callbacks, path) => {
      callbacks.forEach((cb) => {
        try {
          cb(this.get(path), undefined);
        } catch (error) {
          console.error(`Watcher error for ${path}:`, error);
        }
      });
    });
  }
}

/**
 * Global configuration instance
 */
export const globalSignalRConfig = new SignalRConfiguration();
